import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import {
    AlertCircle,
    CalendarDays,
    CheckCircle2,
    Download,
    FileCheck2,
    FileSpreadsheet,
    FileText,
    Filter,
    IndianRupee,
    Link as LinkIcon,
    Printer,
    ReceiptText,
    RefreshCcw,
    Search,
    Send,
    ShieldCheck,
    Trash2,
    UploadCloud
} from 'lucide-react';
import api from '../../utils/api';
import Sidebar from '../../components/Sidebar/sidebar';
import Header from '../../components/Header';
import './invoiceReports.css';
import { API_BASE_URL } from '../../utils/apiBase';
import { getUserRole } from '../../utils/auth';
import { confirmDialog } from '../../components/notifications/notificationService';

const INVOICE_TEMPLATE = {
    companyName: 'I-Pro Infinity Insurance Broking Services',
    companyAddressLines: [
        '#18, 3rd Cross, RMV 2nd Stage,4th Block',
        'MLA Layout, Lottegollahalli, Sanjaynagar',
        'Bengaluru Karnataka 560094',
        'India'
    ],
    companyGstin: 'GSTIN 29AAJFI6371H1ZZ',
    companyPhone: '080 23414133',
    companyEmail: 'ceo@iproinfinity.com',
    companyWebsite: 'www.iproinfinity.com',
    terms: 'Due on Receipt',
    placeOfSupply: 'Karnataka (29)',
    contactPerson: 'S Suresh',
    contactNo: '9986577293',
    bankDetails: {
        accountName: 'SIVAJI',
        bankName: 'ICICI Bank',
        accountNumber: '093705003785',
        ifsc: 'ICIC0000937',
        branch: 'NEW BEL Road Branch, Bangalore'
    }
};

const safeNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const money = (value) => safeNumber(value).toFixed(2);
const formatPercent = (value) => {
    const num = safeNumber(value);
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

const numberToWords = (input) => {
    const num = Math.floor(safeNumber(input));
    if (num === 0) return 'Zero';

    const belowTwenty = [
        '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
        'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen',
        'Eighteen', 'Nineteen'
    ];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const toWords = (n) => {
        if (n < 20) return belowTwenty[n];
        if (n < 100) return `${tens[Math.floor(n / 10)]}${n % 10 ? ` ${belowTwenty[n % 10]}` : ''}`;
        if (n < 1000) return `${belowTwenty[Math.floor(n / 100)]} Hundred${n % 100 ? ` ${toWords(n % 100)}` : ''}`;
        if (n < 100000) return `${toWords(Math.floor(n / 1000))} Thousand${n % 1000 ? ` ${toWords(n % 1000)}` : ''}`;
        if (n < 10000000) return `${toWords(Math.floor(n / 100000))} Lakh${n % 100000 ? ` ${toWords(n % 100000)}` : ''}`;
        return `${toWords(Math.floor(n / 10000000))} Crore${n % 10000000 ? ` ${toWords(n % 10000000)}` : ''}`;
    };

    return toWords(num).trim();
};

const escapeHtml = (value) => (value || '')
    .toString()
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const formatDateDDMMYY = (date = new Date()) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
};

const normalizePolicyNumber = (value) =>
    (value || '').toString().trim().toUpperCase();

const roundMoney = (value) => Math.round((safeNumber(value) + Number.EPSILON) * 100) / 100;

const resolveBrokerageComponents = (row, override) => {
    const basePercentage = safeNumber(row?.['Base Brokerage %']) || safeNumber(row?.['Brokerage %']);
    const storedFinalPercentage = safeNumber(row?.['Brokerage %']);
    const storedAdjustmentType = row?.['Commission Adjustment Type'] || 'add';
    const storedAdjustmentPercentage = row?.['Commission Adjustment %'] !== '' && row?.['Commission Adjustment %'] !== undefined
        ? safeNumber(row?.['Commission Adjustment %'])
        : Math.abs(storedFinalPercentage - basePercentage);

    const adjustmentType = override?.adjustmentType || storedAdjustmentType || 'add';
    const adjustmentPercentage = override?.adjustmentPercentage !== undefined && override?.adjustmentPercentage !== ''
        ? safeNumber(override.adjustmentPercentage)
        : storedAdjustmentPercentage;
    const finalPercentage = roundMoney(
        adjustmentType === 'subtract'
            ? Math.max(basePercentage - adjustmentPercentage, 0)
            : basePercentage + adjustmentPercentage
    );

    return {
        basePercentage: roundMoney(basePercentage),
        adjustmentType,
        adjustmentPercentage: roundMoney(adjustmentPercentage),
        finalPercentage
    };
};

const InvoiceReports = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedInsurer, setSelectedInsurer] = useState('');
    const [selectedPolicyClass, setSelectedPolicyClass] = useState('');
    const [selectedClientType, setSelectedClientType] = useState('');
    const [policyNumberFilter, setPolicyNumberFilter] = useState('');
    const [invoiceStatusFilter, setInvoiceStatusFilter] = useState('pending');
    const [searchTerm, setSearchTerm] = useState('');
    const [insurers, setInsurers] = useState([]);
    const [reportData, setReportData] = useState([]);
    const [selectedRowIds, setSelectedRowIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [hasAppliedFilter, setHasAppliedFilter] = useState(false);
    const [insurerDetails, setInsurerDetails] = useState(null);
    const [uploadType, setUploadType] = useState('excel');
    const [uploadFile, setUploadFile] = useState(null);
    const [statementSettings, setStatementSettings] = useState({
        commissionBasis: 'commissionOnly',
        gstTreatment: 'statementHasGst',
        tdsTreatment: 'statementHasTds',
        gstRate: '18',
        tdsRate: '10',
        tolerance: '1'
    });
    const [uploadInputKey, setUploadInputKey] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadMessage, setUploadMessage] = useState('');
    const [validationResult, setValidationResult] = useState(null);
    const [showValidationModal, setShowValidationModal] = useState(false);
    const [commissionAdjustments, setCommissionAdjustments] = useState({});
    const [savingPolicyBrokerage, setSavingPolicyBrokerage] = useState({});
    const [uploadedDocs, setUploadedDocs] = useState([]);
    const [lockedPolicyNumbers, setLockedPolicyNumbers] = useState([]);
    const [showRemitModal, setShowRemitModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [activeDoc, setActiveDoc] = useState(null);
    const [remitForm, setRemitForm] = useState({
        transactionId: '',
        amount: '',
        date: '',
        remarks: ''
    });

    const visibleColumns = [
        'Date',
        'Client',
        'Client Type',
        'Insurance Company',
        'Policy Class',
        'Policy',
        'Policy Number',
        'Transaction Type',
        'Gross Premium',
        'Net Premium',
        'Brokerage %',
        'Expected Commission',
        'Expected GST on Commission',
        'Expected TDS',
        'Expected Receivable',
        'Invoice Status'
    ];

    useEffect(() => {
        loadInsurers();
        loadUploadedDocs();
    }, []);

    const loadInsurers = async () => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/premium-register/insurers`);
            setInsurers(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchData = async (params = {}) => {
        setLoading(true);
        setError('');
        try {
            const res = await api.get(`${API_BASE_URL}/api/premium-register`, { params });
            const rows = (Array.isArray(res.data) ? res.data : []).map((row, index) => ({
                ...row,
                _rowId: `${index}-${row.Date || ''}-${row.Client || ''}-${row['Policy Number'] || ''}`
            }));
            setReportData(rows);
            setSelectedRowIds([]);
        } catch (err) {
            console.error(err);
            setError('Failed to load invoice transactions.');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyFilter = () => {
        if ((startDate && !endDate) || (!startDate && endDate)) {
            alert('Please select both Start Date and End Date.');
            return;
        }

        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
            alert('Start Date cannot be after End Date.');
            return;
        }

        const params = {};
        if (startDate && endDate) {
            params.start = startDate;
            params.end = endDate;
        }
        if (selectedInsurer) {
            params.insurer = selectedInsurer;
        }

        fetchData(params);
        setHasAppliedFilter(true);
        if (selectedInsurer) {
            loadInsurerDetails(selectedInsurer);
        } else {
            setInsurerDetails(null);
        }
    };

    const handleClearFilter = () => {
        setStartDate('');
        setEndDate('');
        setSelectedInsurer('');
        setSelectedPolicyClass('');
        setSelectedClientType('');
        setPolicyNumberFilter('');
        setInvoiceStatusFilter('pending');
        setReportData([]);
        setHasAppliedFilter(false);
        setInsurerDetails(null);
        setSelectedRowIds([]);
        setUploadFile(null);
        setUploadMessage('');
        setValidationResult(null);
        setCommissionAdjustments({});
        loadUploadedDocs();
    };

    const exportToExcel = () => {
        if (!reportData.length) {
            alert('No data to export');
            return;
        }

        const exportRows = reportData.map(({ _rowId, ...rest }) => rest);
        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoice Reports');

        const fileName = `Invoice_Reports_${startDate || 'ALL'}_to_${endDate || 'ALL'}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    const loadInsurerDetails = async (insurerName) => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/premium-register/insurer-details`, {
                params: { insurerName }
            });
            setInsurerDetails(res.data || null);
        } catch (err) {
            console.error(err);
            setInsurerDetails(null);
        }
    };

    const handleInvoiceDocUpload = async ({ forceProceed = false } = {}) => {
        if (!reportData.length) {
            alert('Load data first before uploading.');
            return;
        }
        if (!uploadFile) {
            alert('Please choose a file to upload.');
            return;
        }

        const selectedRows = reportData
            .filter((row) => selectedRowIds.includes(row._rowId) && !isPolicyLocked(row['Policy Number']));
        if (!selectedRows.length) {
            alert('Please select at least one available policy.');
            return;
        }
        const selectedInsurers = Array.from(new Set(selectedRows.map((row) => row['Insurance Company']).filter(Boolean)));
        if (selectedInsurers.length > 1) {
            alert('Please select policies from one insurer only. Filter by insurer before uploading the statement.');
            return;
        }
        if (!selectedInsurer) {
            alert('Please filter by insurer before uploading the commission statement.');
            return;
        }
        const validationRows = selectedRows
            .map((row) => {
                const expected = getExpectedValues(row);
                return {
                    'Policy Number': row['Policy Number'] || '',
                    Insurer: row['Insurance Company'] || selectedInsurer || '',
                    'Base Brokerage %': row['Base Brokerage %'] || '',
                    'Commission Adjustment Type': resolveBrokerageComponents(row, commissionAdjustments[normalizePolicyNumber(row['Policy Number'])]).adjustmentType,
                    'Commission Adjustment %': resolveBrokerageComponents(row, commissionAdjustments[normalizePolicyNumber(row['Policy Number'])]).adjustmentPercentage,
                    'Brokerage %': expected.brokeragePercentage,
                    'Gross Premium': row['Gross Premium'] || 0,
                    'Net Premium': row['Net Premium'] || 0,
                    'Total Brokerage': expected.expectedCommission,
                    'Expected GST': expected.expectedGst,
                    'Expected TDS': expected.expectedTds
                };
            });
        const selectedTransactionSnapshot = selectedRows.map((row) => {
            const expected = getExpectedValues(row);
            const brokerageComponents = resolveBrokerageComponents(row, commissionAdjustments[normalizePolicyNumber(row['Policy Number'])]);
            return {
                policyNumber: row['Policy Number'] || '',
                clientName: row.Client || '',
                clientType: row['Client Type'] || '',
                insurerName: row['Insurance Company'] || selectedInsurer || '',
                policyClass: row['Policy Class'] || '',
                policyName: row.Policy || '',
                grossPremium: safeNumber(row['Gross Premium']),
                netPremium: safeNumber(row['Net Premium']),
                baseBrokeragePercentage: brokerageComponents.basePercentage,
                commissionAdjustmentType: brokerageComponents.adjustmentType,
                commissionAdjustmentPercentage: brokerageComponents.adjustmentPercentage,
                brokeragePercentage: expected.brokeragePercentage,
                expectedCommission: expected.expectedCommission,
                expectedGst: expected.expectedGst,
                expectedTds: expected.expectedTds,
                expectedReceivable: expected.expectedReceivable
            };
        });

        if (selectedInsurers[0] && selectedInsurer && selectedInsurers[0] !== selectedInsurer) {
            alert('Selected policies do not match the filtered insurer.');
            return;
        }

        if (uploadType === 'excel' && statementSettings.gstTreatment === 'statementHasGst' && safeNumber(statementSettings.gstRate) <= 0) {
            alert('GST rate is required when the statement contains GST.');
            return;
        }

        if (uploadType === 'excel' && statementSettings.tdsTreatment === 'statementHasTds' && safeNumber(statementSettings.tdsRate) <= 0) {
            alert('TDS rate is required when the statement contains TDS.');
            return;
        }

        const formData = new FormData();
        formData.append('invoiceDoc', uploadFile);
        formData.append('fileType', uploadType);
        formData.append('insurerName', selectedInsurer || '');
        formData.append('startDate', startDate || '');
        formData.append('endDate', endDate || '');
        formData.append('selectedTransactionCount', String(selectedRows.length));
        formData.append('validationRows', JSON.stringify(validationRows));
        formData.append('selectedTransactions', JSON.stringify(selectedTransactionSnapshot));
        formData.append('commissionBasis', statementSettings.commissionBasis);
        formData.append('gstTreatment', statementSettings.gstTreatment);
        formData.append('tdsTreatment', statementSettings.tdsTreatment);
        formData.append('gstRate', statementSettings.gstRate);
        formData.append('tdsRate', statementSettings.tdsRate);
        formData.append('tolerance', statementSettings.tolerance);
        formData.append('forceProceed', forceProceed ? 'true' : 'false');

        setUploading(true);
        setUploadMessage('');
        if (!forceProceed) {
            setValidationResult(null);
        }
        try {
            const res = await api.post(`${API_BASE_URL}/api/invoice-reports/upload`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            const uploadedInvoiceId = res?.data?.invoice?._id || '';
            const successMessage = res?.data?.message || 'Invoice document uploaded successfully';
            const summary = res?.data?.invoice?.validationSummary;
            setValidationResult({
                status: summary?.status || 'Validated',
                summary,
                details: res?.data?.invoice?.validationDetails || {},
                mismatches: res?.data?.invoice?.validationDetails?.mismatchedRows || [],
                invoice: res?.data?.invoice || null
            });
            setShowValidationModal(true);
            alert(successMessage);
            setUploadMessage(summary?.status === 'Validated'
                ? `${successMessage}. ${summary.matchedCount || 0} row(s) validated.`
                : successMessage);
            setUploadFile(null);
            setUploadInputKey((prev) => prev + 1);
            setCommissionAdjustments({});
            refreshUploadedDocs();

            if (uploadType === 'excel' && selectedRows.length > 0) {
                const proceedToDownload = await confirmDialog({
                    title: 'Download invoice now?',
                    message: 'Validation and upload succeeded. You can download the invoice for these transactions now.',
                    confirmText: 'Download Invoice',
                    tone: 'info'
                });
                if (proceedToDownload) {
                    await openInvoicePrintWindow(selectedRows, uploadedInvoiceId);
                }
            }
        } catch (err) {
            console.error(err);
            const data = err?.response?.data;
            if (data?.error === 'Excel validation failed' && Array.isArray(data?.mismatches)) {
                const rowSummary = data.mismatches
                    .map((m) =>
                        `Row ${m.rowNumber}${m.sheetName ? ` [${m.sheetName}]` : ''}${m.policyNo ? ` (${m.policyNo})` : ''}: ${m.reasons.join(', ')}`
                    )
                    .join('\n');
                alert(`Excel validation failed.\nMismatched rows: ${data.mismatchCount}\n\n${rowSummary}`);
                setUploadMessage(`Validation failed. ${data.mismatchCount} row(s) mismatched.`);
                setValidationResult({
                    status: data?.validationSummary?.status || 'Failed',
                    summary: data?.validationSummary || {},
                    details: data?.validationDetails || {},
                    mismatches: data?.mismatches || [],
                    invoice: data?.invoice || null
                });
                setShowValidationModal(true);
            } else if (data?.error === 'Some selected transactions are already validated/uploaded' && Array.isArray(data?.lockedPolicyNumbers)) {
                alert(`These transactions are already uploaded/validated:\n${data.lockedPolicyNumbers.join('\n')}`);
                setUploadMessage('Some selected transactions are already uploaded.');
            } else {
                setUploadMessage(data?.error || 'Upload failed.');
                setValidationResult(data?.validationSummary ? {
                    status: data.validationSummary.status || 'Failed',
                    summary: data.validationSummary,
                    details: data.validationDetails || {},
                    mismatches: data.mismatches || [],
                    invoice: data.invoice || null
                } : null);
                if (data?.validationSummary) {
                    setShowValidationModal(true);
                }
                setUploadFile(null);
                setUploadInputKey((prev) => prev + 1);
            }
        } finally {
            setUploading(false);
        }
    };

    const loadUploadedDocs = async (params = {}) => {
        try {
            const res = await api.get(`${API_BASE_URL}/api/invoice-reports/list`, { params });
            const docs = (Array.isArray(res.data) ? res.data : []).filter(
                (d) => !(d.actionType === 'download' || d.fileType === 'download-log')
            );
            setUploadedDocs(docs);
            const locked = Array.from(
                new Set(
                    docs
                        .filter((d) =>
                            d.fileType === 'excel' &&
                            !['Draft', 'Cancelled'].includes(d.status || '') &&
                            d.validationSummary?.status !== 'Failed'
                        )
                        .flatMap((d) => d.validatedPolicyNumbers || [])
                        .map((p) => normalizePolicyNumber(p))
                        .filter(Boolean)
                )
            );
            setLockedPolicyNumbers(locked);
        } catch (err) {
            console.error(err);
            setUploadedDocs([]);
            setLockedPolicyNumbers([]);
        }
    };

    const handleDeleteUploadedDoc = async (id) => {
        const confirmed = await confirmDialog({ title: 'Delete uploaded document?', message: 'The uploaded document will be permanently removed.', confirmText: 'Delete Document' });
        if (!confirmed) {
            return;
        }

        try {
            const res = await api.delete(`${API_BASE_URL}/api/invoice-reports/${id}`);
            alert(res?.data?.message || 'Uploaded document deleted successfully');
            refreshUploadedDocs();
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.error || 'Failed to delete uploaded document');
        }
    };

    const refreshUploadedDocs = () => {
        loadUploadedDocs();
    };

    const handleStatusOptionChange = async (doc, value) => {
        if (value === 'View Details') {
            try {
                const res = await api.get(`${API_BASE_URL}/api/invoice-reports/${doc._id}`);
                setActiveDoc(res.data || doc);
                setShowDetailsModal(true);
            } catch (err) {
                console.error(err);
                alert(err?.response?.data?.error || 'Failed to load details');
            }
            return;
        }

        if (value === 'Remited' || value === 'Partially Received') {
            setActiveDoc(doc);
            setRemitForm({
                transactionId: doc?.remittanceDetails?.transactionId || '',
                amount: doc?.remittanceDetails?.amount ? String(doc.remittanceDetails.amount) : '',
                date: doc?.remittanceDetails?.date ? new Date(doc.remittanceDetails.date).toISOString().slice(0, 10) : '',
                remarks: doc?.remittanceDetails?.remarks || ''
            });
            setShowRemitModal(true);
            return;
        }

        try {
            await api.put(`${API_BASE_URL}/api/invoice-reports/${doc._id}/status`, {
                status: value
            });
            alert('Status updated successfully');
            refreshUploadedDocs();
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.error || 'Failed to update status');
        }
    };

    const submitRemittedStatus = async () => {
        if (!activeDoc?._id) return;
        if (!remitForm.transactionId || !remitForm.amount || !remitForm.date) {
            alert('Transaction ID, Amount and Date are required.');
            return;
        }

        try {
            await api.put(`${API_BASE_URL}/api/invoice-reports/${activeDoc._id}/status`, {
                status: remitForm.amount && activeDoc?.validationSummary?.totalExpectedReceivable
                    ? (Number(remitForm.amount) < Number(activeDoc.validationSummary.totalExpectedReceivable) ? 'Partially Received' : 'Remited')
                    : 'Remited',
                transactionId: remitForm.transactionId,
                amount: Number(remitForm.amount),
                date: remitForm.date,
                remarks: remitForm.remarks
            });
            alert('Status updated successfully');
            setShowRemitModal(false);
            setActiveDoc(null);
            refreshUploadedDocs();
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.error || 'Failed to update status');
        }
    };

    const getDocumentViewUrl = (filePath) => {
        if (!filePath) return '';
        const normalized = filePath.replace(/\\/g, '/');
        const withoutServerPrefix = normalized.startsWith('uploads/')
            ? normalized.slice('uploads/'.length)
            : normalized;
        return `${API_BASE_URL}/uploads/${withoutServerPrefix}`;
    };

    const reserveInvoiceNumber = async (invoiceId, insurerName) => {
        const res = await api.post(`${API_BASE_URL}/api/invoice-reports/${invoiceId}/reserve-number`, {
            insurerName: insurerName || ''
        });
        return res?.data?.invoiceNumber || '';
    };

    const openInvoicePrintWindow = async (rowsOverride = null, invoiceId = '') => {
        if (!hasAppliedFilter) {
            alert('Please apply filters first.');
            return;
        }
        if (!selectedInsurer) {
            alert('Please select an insurer before downloading invoice.');
            return;
        }
        const selectedRows = Array.isArray(rowsOverride)
            ? rowsOverride
            : reportData.filter((row) => selectedRowIds.includes(row._rowId));
        if (!selectedRows.length) {
            alert('Please select at least one transaction.');
            return;
        }
        if (!reportData.length) {
            alert('No records found for selected filters.');
            return;
        }
        if (!invoiceId) {
            alert('Please upload and validate the invoice document before downloading the PDF.');
            return;
        }

        const expectedRows = selectedRows.map((row) => ({
            ...row,
            _expected: getExpectedValues(row)
        }));
        const rateValue = expectedRows.reduce(
            (sum, row) => sum + safeNumber(row._expected.expectedCommission),
            0
        );
        const totalTds = expectedRows.reduce(
            (sum, row) => sum + safeNumber(row._expected.expectedTds),
            0
        );
        const gstPercent = safeNumber(statementSettings.gstRate || 18);
        const cgstPercent = gstPercent / 2;
        const sgstPercent = gstPercent / 2;
        const cgst = rateValue * (cgstPercent / 100);
        const sgst = rateValue * (sgstPercent / 100);
        const amountValue = rateValue;
        const generatedDate = formatDateDDMMYY(new Date());
        const insurerName = insurerDetails?.insurerName || selectedInsurer;
        const insurerAddress = insurerDetails?.address || insurerDetails?.branch || '';
        const insurerGstin = insurerDetails?.gstNumber || '';
        const grandTotal = amountValue + cgst + sgst;
        const netReceivable = grandTotal - totalTds;
        let invoiceNumber = '';

        try {
            invoiceNumber = await reserveInvoiceNumber(invoiceId, insurerName);
        } catch (err) {
            console.error('Failed to reserve invoice number:', err);
            alert('Failed to generate the invoice number. Please try again.');
            return;
        }

        const transactionRowsHtml = expectedRows.map((row, index) => `
          <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml(row['Date'])}</td>
            <td>${escapeHtml(row['Client'])}</td>
            <td>${escapeHtml(row['Client Type'])}</td>
            <td>${escapeHtml(row['Policy'])}</td>
            <td>${escapeHtml(row['Policy Number'])}</td>
            <td class="right">${money(row._expected.expectedCommission)}</td>
            <td class="right">${money(row._expected.expectedGst)}</td>
            <td class="right">${money(row._expected.expectedTds)}</td>
            <td class="right">${money(row._expected.expectedReceivable)}</td>
          </tr>
        `).join('');

        const html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice ${escapeHtml(invoiceNumber)}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 0; color: #000; font-size: 10pt; }
    .invoice-page { width: 190mm; min-height: 277mm; border: 1px solid #555; padding: 3mm; box-sizing: border-box; margin: 0 auto; }
    .border-box { border: 1px solid #555; margin-top: 0; }
    .mt8 { margin-top: 8px; }
    .p8 { padding: 8px; }
    .row { display: flex; }
    .label { font-weight: 700; font-size: 10pt; }
    .small { font-size: 8.5pt; line-height: 1.25; }
    .xsmall { font-size: 8pt; line-height: 1.2; }
    .title { font-size: 18pt; font-weight: 600; text-align: right; white-space: nowrap; }
    .company-name { font-size: 7.5mm; white-space: nowrap; line-height: 1.1; }
    table { width: 100%; border-collapse: collapse; }
    th, td { border: 1px solid #555; padding: 2px 4px; font-size: 9pt; vertical-align: top; }
    .no-border td { border: none; }
    .right { text-align: right; }
    .center { text-align: center; }
    .top-section-left { width: 80%; border-right: 1px solid #555; }
    .top-section-right { width: 20%; display: flex; align-items: center; justify-content: center; }
    .meta-left { width: 50%; border-right: 1px solid #555; }
    .meta-right { width: 50%; }
    .summary-row { display: flex; border-left: 1px solid #555; border-right: 1px solid #555; border-top: none; align-items: stretch; }
    .summary-left { width: 56%; border-right: 1px solid #555; padding: 6px; box-sizing: border-box; }
    .summary-right { width: 44%; display: flex; flex-direction: column; box-sizing: border-box; border-bottom: 1px solid #555; }
    .summary-right-top { border-bottom: 1px solid #555; padding: 4px 0; }
    .summary-right-top table td { border: none; padding: 2px 8px; font-size: 9pt; }
    .summary-right-bottom { margin-top: auto; display: flex; flex-direction: column; justify-content: flex-end; }
    .signature-strip { border-top: none; font-size: 8.5pt; text-align: center; padding: 2px 0; }
    .bill-to-label { margin-top: 8px; margin-bottom: 2px; padding-left: 2px; }
    .page-break { page-break-before: always; break-before: page; }
    .page-two-title { font-size: 12pt; font-weight: 700; margin-bottom: 6px; }
    .page-two th, .page-two td { font-size: 8.5pt; }
    @media print {
      @page { size: A4; margin: 0; }
      body { margin: 0; }
      .invoice-page { width: 190mm; min-height: 277mm; margin: 0 auto; }
    }
  </style>
</head>
<body>
  <div class="invoice-page">
    <div class="border-box row">
      <div class="top-section-left p8">
        <div class="label company-name">${escapeHtml(INVOICE_TEMPLATE.companyName)}</div>
        ${INVOICE_TEMPLATE.companyAddressLines.map(line => `<div class="small">${escapeHtml(line)}</div>`).join('')}
        <div class="small">${escapeHtml(INVOICE_TEMPLATE.companyGstin)}</div>
        <div class="small">${escapeHtml(INVOICE_TEMPLATE.companyPhone)}</div>
        <div class="small">${escapeHtml(INVOICE_TEMPLATE.companyEmail)}</div>
        <div class="small">${escapeHtml(INVOICE_TEMPLATE.companyWebsite)}</div>
      </div>
      <div class="top-section-right">
        <div class="title">Tax Invoice</div>
      </div>
    </div>

    <div class="border-box row">
      <div class="meta-left">
        <table>
          <tr><td class="label" style="width:90px;">#</td><td class="label">: ${escapeHtml(invoiceNumber)}</td></tr>
          <tr><td class="label">Invoice Date</td><td class="label">: ${escapeHtml(generatedDate)}</td></tr>
          <tr><td class="label">Terms</td><td class="label">: ${escapeHtml(INVOICE_TEMPLATE.terms)}</td></tr>
          <tr><td class="label">Due Date</td><td class="label">: ${escapeHtml(generatedDate)}</td></tr>
        </table>
      </div>
      <div class="meta-right">
        <div class="p8">
          <span class="label">Place Of Supply</span>
          <span class="label"> : ${escapeHtml(INVOICE_TEMPLATE.placeOfSupply)}</span>
        </div>
      </div>
    </div>

    <div class="label bill-to-label">Bill To</div>
    <div class="border-box p8">
      <div class="label">${escapeHtml(insurerName)}</div>
      <div class="small">${escapeHtml(insurerAddress)}</div>
      <div class="small">India</div>
      <div class="small">GSTIN ${escapeHtml(insurerGstin)}</div>
    </div>

    <table style="margin-top:8px;">
      <thead>
        <tr>
          <th rowspan="2" style="width:5%;">#</th>
          <th rowspan="2" style="width:35%;">Item &amp; Description</th>
          <th rowspan="2" style="width:12%;" class="right">Rate</th>
          <th colspan="2" style="width:22%;" class="center">CGST</th>
          <th colspan="2" style="width:22%;" class="center">SGST</th>
          <th rowspan="2" style="width:14%;" class="right">Amount</th>
        </tr>
        <tr>
          <th class="center">%</th>
          <th class="right">Amt</th>
          <th class="center">%</th>
          <th class="right">Amt</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>1</td>
          <td>Insurance Commission\`</td>
          <td class="right">${money(rateValue)}</td>
          <td class="center">${formatPercent(cgstPercent)}%</td>
          <td class="right">${money(cgst)}</td>
          <td class="center">${formatPercent(sgstPercent)}%</td>
          <td class="right">${money(sgst)}</td>
          <td class="right">${money(amountValue)}</td>
        </tr>
      </tbody>
    </table>

    <div class="summary-row">
      <div class="summary-left">
        <div class="small">Total in Words</div>
        <div class="small"><b><i>Indian Rupee ${escapeHtml(numberToWords(grandTotal))} Only</i></b></div>
        <div style="margin-top: 8px;" class="small">Notes</div>
        <div class="small">Contact Person: ${escapeHtml(INVOICE_TEMPLATE.contactPerson)}</div>
        <div class="small">Contact No: ${escapeHtml(INVOICE_TEMPLATE.contactNo)}</div>
        <div class="small">TDS Reference: Deduct as applicable under Income Tax provisions.</div>
        <div style="margin-top: 8px;" class="small"><b>COMPANY BANK ACCOUNT DETAILS.</b></div>
        <div class="small">Bank Account No: ${escapeHtml(INVOICE_TEMPLATE.bankDetails.accountNumber)}</div>
        <div class="small">Bank Name: ${escapeHtml(INVOICE_TEMPLATE.bankDetails.bankName)}</div>
        <div class="small">Branch Name: ${escapeHtml(INVOICE_TEMPLATE.bankDetails.branch)}</div>
        <div class="small">IFSC Code: ${escapeHtml(INVOICE_TEMPLATE.bankDetails.ifsc)}</div>
      </div>
      <div class="summary-right">
        <div class="summary-right-top">
          <table class="no-border">
            <tr><td>CGST (${formatPercent(cgstPercent)}%)</td><td class="right">${money(cgst)}</td></tr>
            <tr><td>SGST (${formatPercent(sgstPercent)}%)</td><td class="right">${money(sgst)}</td></tr>
            <tr><td class="label">Total</td><td class="right label">${money(grandTotal)}</td></tr>
            <tr><td>TDS</td><td class="right">${money(totalTds)}</td></tr>
            <tr><td class="label">Net Receivable</td><td class="right label">${money(netReceivable)}</td></tr>
          </table>
        </div>
        <div class="summary-right-bottom">
          <div class="signature-strip">Authorized Signature</div>
        </div>
      </div>
    </div>
  </div>

  <div class="invoice-page page-break page-two">
    <div class="page-two-title">Transaction Details</div>
    <table>
      <thead>
        <tr>
          <th style="width:5%;">#</th>
          <th style="width:10%;">Date</th>
          <th style="width:20%;">Client</th>
          <th style="width:10%;">Client Type</th>
          <th style="width:18%;">Policy</th>
          <th style="width:15%;">Policy Number</th>
          <th style="width:12%;">Taxable Commission</th>
          <th style="width:10%;">GST</th>
          <th style="width:10%;">TDS</th>
          <th style="width:10%;">Net Receivable</th>
        </tr>
      </thead>
      <tbody>
        ${transactionRowsHtml}
      </tbody>
    </table>
  </div>
</body>
</html>`;

        api.post(`${API_BASE_URL}/api/invoice-reports/download-save`, {
            invoiceId,
            invoiceHtml: html,
            insurerName: insurerName || '',
            startDate: startDate || '',
            endDate: endDate || '',
            selectedTransactionCount: selectedRows.length,
            invoiceNumber
        }).then(() => {
            refreshUploadedDocs();
        }).catch((err) => {
            console.error('Failed to save downloaded invoice PDF:', err);
        });

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Popup blocked. Please allow popups and try again.');
            return;
        }

        printWindow.document.open();
        printWindow.document.write(html);
        printWindow.document.close();
        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
        }, 500);
    };

    const isPolicyLocked = useCallback(
        (policyNumber) => lockedPolicyNumbers.includes(normalizePolicyNumber(policyNumber)),
        [lockedPolicyNumbers]
    );

    const getPolicyInvoiceStatus = useCallback((policyNumber) => {
        const normalized = normalizePolicyNumber(policyNumber);
        if (!normalized) return 'Pending Invoice';

        const linkedDoc = uploadedDocs.find((doc) =>
            !['Draft', 'Cancelled'].includes(doc.status || '') &&
            doc.validationSummary?.status !== 'Failed' &&
            (doc.validatedPolicyNumbers || []).some((item) => normalizePolicyNumber(item) === normalized)
        );

        if (!linkedDoc) return 'Pending Invoice';
        if (linkedDoc.status === 'Remited') return 'Remitted';
        return 'Already Invoiced';
    }, [uploadedDocs]);

    const getAdjustedBrokeragePercentage = useCallback((row) => {
        const key = normalizePolicyNumber(row?.['Policy Number']);
        return resolveBrokerageComponents(row, commissionAdjustments[key]).finalPercentage;
    }, [commissionAdjustments]);

    const getExpectedValues = useCallback((row) => {
        const netPremium = safeNumber(row['Net Premium']);
        const brokeragePercentage = getAdjustedBrokeragePercentage(row);
        const storedBrokerage = safeNumber(row['Total Brokerage']);
        const expectedCommission = brokeragePercentage > 0
            ? roundMoney((netPremium * brokeragePercentage) / 100)
            : storedBrokerage;
        const expectedGst = roundMoney(expectedCommission * (safeNumber(statementSettings.gstRate) / 100));
        const expectedTds = roundMoney(expectedCommission * (safeNumber(statementSettings.tdsRate) / 100));

        return {
            expectedCommission: roundMoney(expectedCommission),
            expectedGst,
            expectedTds,
            expectedReceivable: roundMoney(expectedCommission + expectedGst - expectedTds),
            brokeragePercentage
        };
    }, [getAdjustedBrokeragePercentage, statementSettings.gstRate, statementSettings.tdsRate]);

    const policyClassOptions = useMemo(() => {
        return Array.from(new Set(reportData.map((row) => row['Policy Class']).filter(Boolean))).sort();
    }, [reportData]);

    const filteredReportData = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        const policyNumberQuery = policyNumberFilter.trim().toLowerCase();

        return reportData.filter((row) => {
            const status = getPolicyInvoiceStatus(row['Policy Number']);
            const matchesSearch = !query || [
            row.Client,
            row['Policy Number'],
            row.Policy,
            row['Insurance Company'],
            row['Client Type'],
            row['Mode of Payments']
            ].join(' ').toLowerCase().includes(query);

            const matchesPolicyNumber =
                !policyNumberQuery ||
                String(row['Policy Number'] || '').toLowerCase().includes(policyNumberQuery);
            const matchesPolicyClass =
                !selectedPolicyClass || row['Policy Class'] === selectedPolicyClass;
            const matchesClientType =
                !selectedClientType || row['Client Type'] === selectedClientType;
            const matchesInvoiceStatus =
                invoiceStatusFilter === 'all' ||
                (invoiceStatusFilter === 'pending' && status === 'Pending Invoice') ||
                (invoiceStatusFilter === 'invoiced' && status === 'Already Invoiced') ||
                (invoiceStatusFilter === 'remitted' && status === 'Remitted');

            return matchesSearch &&
                matchesPolicyNumber &&
                matchesPolicyClass &&
                matchesClientType &&
                matchesInvoiceStatus;
        });
    }, [
        reportData,
        searchTerm,
        policyNumberFilter,
        selectedPolicyClass,
        selectedClientType,
        invoiceStatusFilter,
        getPolicyInvoiceStatus
    ]);

    const selectedRows = useMemo(
        () => filteredReportData.filter((row) => selectedRowIds.includes(row._rowId) && !isPolicyLocked(row['Policy Number'])),
        [filteredReportData, selectedRowIds, isPolicyLocked]
    );

    const invoiceSummary = useMemo(() => {
        const totalPremium = filteredReportData.reduce((sum, row) => sum + safeNumber(row['Gross Premium']), 0);
        const brokerage = selectedRows.reduce((sum, row) => sum + safeNumber(getExpectedValues(row).expectedCommission), 0);
        const remitedDocs = uploadedDocs.filter((doc) => doc.status === 'Remited').length;

        return {
            totalTransactions: filteredReportData.length,
            selectedTransactions: selectedRows.length,
            lockedTransactions: filteredReportData.filter((row) => isPolicyLocked(row['Policy Number'])).length,
            totalPremium,
            brokerage,
            uploadedCount: uploadedDocs.length,
            pendingDocs: uploadedDocs.length - remitedDocs,
            remitedDocs
        };
    }, [filteredReportData, selectedRows, uploadedDocs, isPolicyLocked, getExpectedValues]);

    const isAdminUser = getUserRole() === 'admin';
    const validationDetails = useMemo(() => validationResult?.details || {}, [validationResult]);
    const validationMismatchRows = useMemo(
        () => validationResult?.mismatches || validationDetails.mismatchedRows || [],
        [validationResult, validationDetails]
    );
    const validationMatchedRows = useMemo(() => validationDetails.matchedRows || [], [validationDetails]);
    const validationMissingPolicies = useMemo(() => validationDetails.missingPolicies || [], [validationDetails]);
    const validationExtraRows = useMemo(() => validationDetails.extraRows || [], [validationDetails]);
    const validationAmountDifferences = useMemo(() => validationDetails.amountDifferences || [], [validationDetails]);
    const commissionAdjustmentRows = useMemo(() => {
        return validationAmountDifferences
            .filter((item) => String(item.type || '').toLowerCase().includes('commission'))
            .map((item) => {
                const policyNo = normalizePolicyNumber(item.policyNo);
                const row = reportData.find((entry) => normalizePolicyNumber(entry['Policy Number']) === policyNo) || {};
                const netPremium = safeNumber(row['Net Premium']);
                const gstRate = safeNumber(statementSettings.gstRate);
                const tdsRate = safeNumber(statementSettings.tdsRate);
                let taxableStatementCommission = safeNumber(item.statement);

                if (statementSettings.commissionBasis === 'commissionIncludesGst') {
                    taxableStatementCommission = gstRate > 0
                        ? taxableStatementCommission / (1 + (gstRate / 100))
                        : taxableStatementCommission;
                } else if (statementSettings.commissionBasis === 'netPayable') {
                    const gstFactor = statementSettings.gstTreatment === 'statementHasGst' ? gstRate / 100 : 0;
                    const tdsFactor = statementSettings.tdsTreatment === 'statementHasTds' ? tdsRate / 100 : 0;
                    const payableFactor = 1 + gstFactor - tdsFactor;
                    taxableStatementCommission = payableFactor > 0
                        ? taxableStatementCommission / payableFactor
                        : taxableStatementCommission;
                }

                const suggestedPercentage = netPremium > 0
                    ? roundMoney((taxableStatementCommission / netPremium) * 100)
                    : 0;
                const currentExpected = getExpectedValues(row);
                const currentComponents = resolveBrokerageComponents(row, commissionAdjustments[policyNo]);
                const suggestedAdjustmentType = suggestedPercentage >= currentComponents.basePercentage ? 'add' : 'subtract';
                const suggestedAdjustmentPercentage = roundMoney(Math.abs(suggestedPercentage - currentComponents.basePercentage));

                return {
                    ...item,
                    policyNo,
                    row,
                    policyId: row._policyId || '',
                    netPremium,
                    taxableStatementCommission: roundMoney(taxableStatementCommission),
                    basePercentage: currentComponents.basePercentage,
                    currentAdjustmentType: currentComponents.adjustmentType,
                    currentAdjustmentPercentage: currentComponents.adjustmentPercentage,
                    currentPercentage: currentComponents.finalPercentage,
                    currentExpectedCommission: currentExpected.expectedCommission,
                    suggestedPercentage,
                    suggestedAdjustmentType,
                    suggestedAdjustmentPercentage,
                    adjustedPercentage: commissionAdjustments[policyNo] ?? ''
                };
            });
    }, [
        validationAmountDifferences,
        reportData,
        statementSettings.commissionBasis,
        statementSettings.gstRate,
        statementSettings.gstTreatment,
        statementSettings.tdsRate,
        statementSettings.tdsTreatment,
        getExpectedValues,
        commissionAdjustments
    ]);

    const formatCurrency = (value) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value || 0);

    const savePolicyBrokerageAdjustment = async (item) => {
        const policyId = item.policyId;
        const policyNo = item.policyNo;
        const brokerageComponents = resolveBrokerageComponents(item.row, commissionAdjustments[policyNo]);

        if (!policyId) {
            alert('Policy ID is missing for this row. Please reload the report and try again.');
            return;
        }
        if (brokerageComponents.adjustmentPercentage < 0) {
            alert('Adjustment percentage cannot be negative.');
            return;
        }

        setSavingPolicyBrokerage((current) => ({ ...current, [policyNo]: true }));
        try {
            const response = await api.put(`${API_BASE_URL}/api/invoice-reports/policies/${policyId}/brokerage`, {
                commissionAdjustmentType: brokerageComponents.adjustmentType,
                commissionAdjustmentPercentage: brokerageComponents.adjustmentPercentage
            });
            const updatedPolicy = response.data?.policy || {};
            setReportData((rows) => rows.map((row) => {
                if (String(row._policyId || '') !== String(policyId)) return row;
                return {
                    ...row,
                    'Base Brokerage %': updatedPolicy.baseBrokeragePercentage ?? brokerageComponents.basePercentage,
                    'Commission Adjustment Type': updatedPolicy.commissionAdjustmentType ?? brokerageComponents.adjustmentType,
                    'Commission Adjustment %': updatedPolicy.commissionAdjustmentPercentage ?? brokerageComponents.adjustmentPercentage,
                    'Brokerage %': updatedPolicy.brokeragePercentage ?? brokerageComponents.finalPercentage,
                    'Total Brokerage': updatedPolicy.totalBrokerage ?? roundMoney((safeNumber(row['Net Premium']) * brokerageComponents.finalPercentage) / 100)
                };
            }));
            setCommissionAdjustments((current) => {
                const next = { ...current };
                delete next[policyNo];
                return next;
            });
            alert('Brokerage percentage updated in the main policy.');
        } catch (err) {
            console.error(err);
            alert(err?.response?.data?.error || 'Failed to update brokerage percentage.');
        } finally {
            setSavingPolicyBrokerage((current) => ({ ...current, [policyNo]: false }));
        }
    };

    const selectableRowIds = filteredReportData
        .filter((row) => !isPolicyLocked(row['Policy Number']))
        .map((row) => row._rowId);
    const isAllSelected = selectableRowIds.length > 0 && selectableRowIds.every((id) => selectedRowIds.includes(id));

    const toggleSelectAll = (checked) => {
        if (checked) {
            const selectedInsurers = Array.from(new Set(
                filteredReportData
                    .filter((row) => selectableRowIds.includes(row._rowId))
                    .map((row) => row['Insurance Company'])
                    .filter(Boolean)
            ));
            if (selectedInsurers.length > 1) {
                alert('Please filter by one insurer before selecting all policies for an invoice batch.');
                return;
            }
            setSelectedRowIds(selectableRowIds);
            return;
        }
        setSelectedRowIds([]);
    };
    const toggleRowSelection = (rowId, checked) => {
        if (checked) {
            const nextRow = filteredReportData.find((row) => row._rowId === rowId);
            const nextInsurer = nextRow?.['Insurance Company'] || '';
            const currentSelectedRows = filteredReportData.filter((row) => selectedRowIds.includes(row._rowId));
            const currentInsurers = Array.from(new Set(currentSelectedRows.map((row) => row['Insurance Company']).filter(Boolean)));

            if (currentInsurers.length && nextInsurer && !currentInsurers.includes(nextInsurer)) {
                alert('One invoice batch can contain policies from one insurer only. Clear selection or filter by that insurer.');
                return;
            }
            setSelectedRowIds((prev) => [...prev, rowId]);
            return;
        }
        setSelectedRowIds((prev) => prev.filter((id) => id !== rowId));
    };

    useEffect(() => {
        if (!reportData.length) {
            setSelectedRowIds([]);
            return;
        }
        const allowed = reportData
            .filter((row) => !isPolicyLocked(row['Policy Number']))
            .map((row) => row._rowId);
        setSelectedRowIds((prev) => prev.filter((id) => allowed.includes(id)));
    }, [reportData, lockedPolicyNumbers, isPolicyLocked]);

    return (
        <div className="invoice-reports-container invoice-page-shell">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`invoice-reports-content ${collapsed ? 'collapsed' : ''}`}>
                <div className="invoice-reports-sticky-header">
                    <Header />
                </div>

                <div className="invoice-reports-scroll-area">
                    <section className="invoice-report-section">
                        <div className="invoice-hero">
                            <div>
                                <div className="invoice-eyebrow"><ReceiptText size={15} /> Reports Workspace</div>
                                <h1>Invoice Management</h1>
                                <p>Validate premium transactions, upload invoice statements, generate tax invoices, and track commission remittance from one focused workspace.</p>
                            </div>
                            <div className="invoice-hero-actions">
                                <span>{hasAppliedFilter ? 'Filters applied' : 'Awaiting filter'}</span>
                                <strong>{invoiceSummary.selectedTransactions}</strong>
                                <small>transactions selected</small>
                            </div>
                        </div>

                        <div className="invoice-summary-grid">
                            <div className="invoice-summary-card is-blue">
                                <FileText size={22} />
                                <span>Transactions</span>
                                <strong>{invoiceSummary.totalTransactions}</strong>
                                <small>{reportData.length} loaded</small>
                            </div>
                            <div className="invoice-summary-card is-green">
                                <CheckCircle2 size={22} />
                                <span>Selected</span>
                                <strong>{invoiceSummary.selectedTransactions}</strong>
                                <small>ready for validation</small>
                            </div>
                            <div className="invoice-summary-card is-amber">
                                <ShieldCheck size={22} />
                                <span>Locked</span>
                                <strong>{invoiceSummary.lockedTransactions}</strong>
                                <small>already validated</small>
                            </div>
                            <div className="invoice-summary-card is-teal">
                                <IndianRupee size={22} />
                                <span>Total Premium</span>
                                <strong>{formatCurrency(invoiceSummary.totalPremium)}</strong>
                                <small>visible transactions</small>
                            </div>
                            <div className="invoice-summary-card is-indigo">
                                <ReceiptText size={22} />
                                <span>Brokerage</span>
                                <strong>{formatCurrency(invoiceSummary.brokerage)}</strong>
                                <small>selected rows</small>
                            </div>
                            <div className="invoice-summary-card is-rose">
                                <UploadCloud size={22} />
                                <span>Uploaded Docs</span>
                                <strong>{invoiceSummary.uploadedCount}</strong>
                                <small>{invoiceSummary.remitedDocs} remited</small>
                            </div>
                        </div>

                        <div className="invoice-filter-card">
                            <div className="invoice-card-heading">
                                <div>
                                    <h2><Filter size={18} /> Transaction Filters</h2>
                                    <p>Choose a period and insurer, then select the transactions to validate and invoice.</p>
                                </div>
                                <div className="invoice-search">
                                    <Search size={18} />
                                    <input
                                        type="search"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder="Search policy, client, insurer, payment mode"
                                    />
                                </div>
                            </div>

                            {error && <div className="invoice-report-error"><AlertCircle size={16} /> {error}</div>}

                            <div className="invoice-report-filters">
                                <label>
                                    <span>Start Date</span>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </label>

                                <label>
                                    <span>End Date</span>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </label>

                                <label>
                                    <span>Insurer</span>
                                    <select
                                        value={selectedInsurer}
                                        onChange={(e) => setSelectedInsurer(e.target.value)}
                                    >
                                        <option value="">All Insurers</option>
                                        {insurers.map((insurer) => (
                                            <option key={insurer} value={insurer}>
                                                {insurer}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <label>
                                    <span>Policy Type / Class</span>
                                    <select
                                        value={selectedPolicyClass}
                                        onChange={(e) => setSelectedPolicyClass(e.target.value)}
                                    >
                                        <option value="">All Classes</option>
                                        {policyClassOptions.map((policyClass) => (
                                            <option key={policyClass} value={policyClass}>
                                                {policyClass}
                                            </option>
                                        ))}
                                    </select>
                                </label>

                                <label>
                                    <span>Client Type</span>
                                    <select
                                        value={selectedClientType}
                                        onChange={(e) => setSelectedClientType(e.target.value)}
                                    >
                                        <option value="">All Client Types</option>
                                        <option value="Retail">Retail</option>
                                        <option value="Corporate">Corporate</option>
                                    </select>
                                </label>

                                <label>
                                    <span>Policy Number</span>
                                    <input
                                        type="search"
                                        value={policyNumberFilter}
                                        onChange={(e) => setPolicyNumberFilter(e.target.value)}
                                        placeholder="Policy number"
                                    />
                                </label>

                                <label>
                                    <span>Invoice Status</span>
                                    <select
                                        value={invoiceStatusFilter}
                                        onChange={(e) => setInvoiceStatusFilter(e.target.value)}
                                    >
                                        <option value="pending">Pending Invoice</option>
                                        <option value="invoiced">Already Invoiced</option>
                                        <option value="remitted">Remitted</option>
                                        <option value="all">All Statuses</option>
                                    </select>
                                </label>
                            </div>

                            <div className="invoice-action-row">
                                <button className="invoice-btn invoice-btn-primary" onClick={handleApplyFilter} disabled={loading}>
                                    {loading ? <RefreshCcw className="invoice-spin" size={17} /> : <CalendarDays size={17} />}
                                    {loading ? 'Loading...' : 'Generate Report'}
                                </button>
                                <button className="invoice-btn invoice-btn-light" onClick={handleClearFilter} disabled={loading}>
                                    <RefreshCcw size={17} /> Reset
                                </button>
                                <button className="invoice-btn invoice-btn-success" onClick={exportToExcel} disabled={!reportData.length}>
                                    <FileSpreadsheet size={17} /> Export Excel
                                </button>
                            </div>
                        </div>

                        <div className="invoice-table-card">
                            <div className="invoice-card-heading">
                                <div>
                                    <h2><FileCheck2 size={18} /> Invoice Transactions</h2>
                                    <p>{filteredReportData.length} visible rows, {selectableRowIds.length} available for selection.</p>
                                </div>
                                <span className="invoice-selection-pill">{invoiceSummary.selectedTransactions} selected</span>
                            </div>

                            <div className="invoice-report-table-wrap">
                                <table className="invoice-report-table">
                                    <thead>
                                        <tr>
                                            <th className="invoice-check-col">
                                                <input
                                                    type="checkbox"
                                                    checked={isAllSelected}
                                                    onChange={(e) => toggleSelectAll(e.target.checked)}
                                                    disabled={!selectableRowIds.length}
                                                />
                                            </th>
                                            {visibleColumns.map((key) => (
                                                <th key={key}>{key}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            Array.from({ length: 7 }).map((_, rowIndex) => (
                                                <tr key={`invoice-loading-${rowIndex}`} className="invoice-skeleton-row">
                                                    <td><span /></td>
                                                    {visibleColumns.map((col) => <td key={col}><span /></td>)}
                                                </tr>
                                            ))
                                        ) : filteredReportData.length === 0 ? (
                                            <tr>
                                                <td colSpan={visibleColumns.length + 1} className="invoice-empty-state">
                                                    <ReceiptText size={34} />
                                                    <strong>No invoice transactions found</strong>
                                                    <span>Apply filters or widen your search to load transactions.</span>
                                                </td>
                                            </tr>
                                        ) : filteredReportData.map((row, rowIndex) => (
                                            <tr key={row._rowId || rowIndex} className={isPolicyLocked(row['Policy Number']) ? 'invoice-row-locked' : ''}>
                                                <td className="invoice-check-col">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRowIds.includes(row._rowId)}
                                                        disabled={isPolicyLocked(row['Policy Number'])}
                                                        onChange={(e) => toggleRowSelection(row._rowId, e.target.checked)}
                                                    />
                                                </td>
                                                {visibleColumns.map((col) => (
                                                    <td key={col} data-label={col}>
                                                        {(() => {
                                                            const expected = getExpectedValues(row);
                                                            if (['Gross Premium', 'Net Premium'].includes(col)) {
                                                                return formatCurrency(safeNumber(row[col]));
                                                            }
                                                            if (col === 'Brokerage %') return `${formatPercent(expected.brokeragePercentage)}%`;
                                                            if (col === 'Expected Commission') return formatCurrency(expected.expectedCommission);
                                                            if (col === 'Expected GST on Commission') return formatCurrency(expected.expectedGst);
                                                            if (col === 'Expected TDS') return formatCurrency(expected.expectedTds);
                                                            if (col === 'Expected Receivable') return formatCurrency(expected.expectedReceivable);
                                                            if (col === 'Invoice Status') {
                                                                const status = getPolicyInvoiceStatus(row['Policy Number']);
                                                                return (
                                                                    <span className={`invoice-status-badge ${status === 'Remitted'
                                                                        ? 'status-remited'
                                                                        : status === 'Already Invoiced'
                                                                            ? 'status-partial'
                                                                            : 'status-pending'
                                                                    }`}>
                                                                        {status}
                                                                    </span>
                                                                );
                                                            }
                                                            return row[col] || '-';
                                                        })()}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {reportData.length > 0 && (
                            <div className="invoice-upload-box">
                                <div className="invoice-card-heading">
                                    <div>
                                        <h2><UploadCloud size={18} /> Upload Commission Statement</h2>
                                        <p>Upload the insurer statement for the selected booked policies and validate commission, GST, TDS, and receivable values.</p>
                                    </div>
                                    <span className="invoice-selection-pill">{invoiceSummary.selectedTransactions} selected</span>
                                </div>
                                <div className="invoice-upload-row">
                                    <label>
                                        <span>File Type</span>
                                        <select
                                            value={uploadType}
                                            onChange={(e) => {
                                                setUploadType(e.target.value);
                                                setUploadFile(null);
                                                setUploadMessage('');
                                            }}
                                        >
                                            <option value="excel">Excel</option>
                                            <option value="pdf">PDF</option>
                                        </select>
                                    </label>

                                    <label>
                                        <span>Statement Type</span>
                                        <select
                                            value={statementSettings.commissionBasis}
                                            onChange={(e) => setStatementSettings((prev) => ({ ...prev, commissionBasis: e.target.value }))}
                                            disabled={uploadType !== 'excel'}
                                        >
                                            <option value="commissionOnly">Commission Excludes GST/TDS</option>
                                            <option value="commissionIncludesGst">Commission Includes GST</option>
                                            <option value="netPayable">Only Net Payable Shown</option>
                                        </select>
                                    </label>

                                    <label>
                                        <span>GST In Statement</span>
                                        <select
                                            value={statementSettings.gstTreatment}
                                            onChange={(e) => setStatementSettings((prev) => ({ ...prev, gstTreatment: e.target.value }))}
                                            disabled={uploadType !== 'excel'}
                                        >
                                            <option value="statementHasGst">GST Included / Shown</option>
                                            <option value="statementWithoutGst">GST Not Included</option>
                                        </select>
                                    </label>

                                    <label>
                                        <span>TDS In Statement</span>
                                        <select
                                            value={statementSettings.tdsTreatment}
                                            onChange={(e) => setStatementSettings((prev) => ({ ...prev, tdsTreatment: e.target.value }))}
                                            disabled={uploadType !== 'excel'}
                                        >
                                            <option value="statementHasTds">TDS Included / Shown</option>
                                            <option value="statementWithoutTds">TDS Not Included</option>
                                        </select>
                                    </label>

                                    <label>
                                        <span>GST %</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={statementSettings.gstRate}
                                            onChange={(e) => setStatementSettings((prev) => ({ ...prev, gstRate: e.target.value }))}
                                            disabled={uploadType !== 'excel'}
                                        />
                                    </label>

                                    <label>
                                        <span>TDS %</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={statementSettings.tdsRate}
                                            onChange={(e) => setStatementSettings((prev) => ({ ...prev, tdsRate: e.target.value }))}
                                            disabled={uploadType !== 'excel'}
                                        />
                                    </label>

                                    <label>
                                        <span>Tolerance</span>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={statementSettings.tolerance}
                                            onChange={(e) => setStatementSettings((prev) => ({ ...prev, tolerance: e.target.value }))}
                                            disabled={uploadType !== 'excel'}
                                        />
                                    </label>

                                    <label className="invoice-file-control">
                                        <span>Choose File</span>
                                        <input
                                            key={uploadInputKey}
                                            type="file"
                                            accept={uploadType === 'excel' ? '.xlsx,.xls' : '.pdf'}
                                            onChange={(e) => {
                                                setUploadFile(e.target.files?.[0] || null);
                                                setUploadMessage('');
                                                setValidationResult(null);
                                                setCommissionAdjustments({});
                                            }}
                                        />
                                    </label>

                                    <div>
                                        <button className="invoice-btn invoice-btn-primary" onClick={handleInvoiceDocUpload} disabled={uploading || !uploadFile}>
                                            {uploading ? <RefreshCcw className="invoice-spin" size={17} /> : <Send size={17} />}
                                            {uploading ? 'Uploading...' : 'Upload & Validate'}
                                        </button>
                                    </div>
                                </div>

                                {uploadMessage && <div className="invoice-upload-message">{uploadMessage}</div>}
                                {validationResult && (
                                    <div className="invoice-validation-launch">
                                        <span>Validation result: <strong>{validationResult.status}</strong></span>
                                        <button
                                            type="button"
                                            className="invoice-btn invoice-btn-light"
                                            onClick={() => setShowValidationModal(true)}
                                        >
                                            <ShieldCheck size={17} />
                                            View Validation Review
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {showValidationModal && validationResult && (
                            <div className="invoice-modal-backdrop">
                                <div className="invoice-modal invoice-validation-modal">
                                    <div className="invoice-validation-panel">
                                        <div className="invoice-card-heading">
                                            <div>
                                                <h2><ShieldCheck size={18} /> Validation Review</h2>
                                                <p>Review matched rows, mismatches, missing policies, extra statement rows, and amount differences before invoice generation.</p>
                                            </div>
                                            <span className={`invoice-status-badge ${validationResult.status === 'Validated' || validationResult.status === 'Validated With Warning'
                                                ? 'status-remited'
                                                : 'status-cancelled'
                                            }`}>
                                                {validationResult.status}
                                            </span>
                                        </div>

                                        <div className="invoice-validation-summary">
                                            <span>Matched <strong>{validationResult.summary?.matchedCount || validationMatchedRows.length || 0}</strong></span>
                                            <span>Mismatched <strong>{validationResult.summary?.mismatchCount || validationMismatchRows.length || 0}</strong></span>
                                            <span>Missing <strong>{validationResult.summary?.missingCount || validationMissingPolicies.length || 0}</strong></span>
                                            <span>Extra <strong>{validationResult.summary?.extraCount || validationExtraRows.length || 0}</strong></span>
                                        </div>

                                        {validationMismatchRows.length > 0 && (
                                            <div className="invoice-validation-block">
                                                <h3>Mismatched Rows</h3>
                                                <div className="invoice-validation-list">
                                                    {validationMismatchRows.slice(0, 12).map((item, index) => (
                                                        <div key={`${item.policyNo || 'row'}-${index}`} className="invoice-validation-item">
                                                            <strong>{item.policyNo || `Row ${item.rowNumber || '-'}`}</strong>
                                                            <span>{(item.statuses || []).join(', ') || 'Mismatch'}</span>
                                                            <small>{(item.reasons || []).join(' | ')}</small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {validationAmountDifferences.length > 0 && (
                                            <div className="invoice-validation-block">
                                                <h3>Amount Differences</h3>
                                                <div className="invoice-difference-grid">
                                                    {validationAmountDifferences.slice(0, 12).map((item, index) => (
                                                        <div key={`${item.policyNo}-${item.type}-${index}`}>
                                                            <span>{item.policyNo || '-'}</span>
                                                            <strong>{item.type}</strong>
                                                            <small>Statement {formatCurrency(item.statement)} / Expected {formatCurrency(item.expected)} / Diff {formatCurrency(item.difference)}</small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {commissionAdjustmentRows.length > 0 && (
                                            <div className="invoice-validation-block">
                                                <h3>Commission Adjustment - Updates Main Policy</h3>
                                                <div className="invoice-adjustment-grid">
                                                    {commissionAdjustmentRows.map((item, index) => (
                                                        <div className="invoice-adjustment-card" key={`${item.policyNo}-${index}`}>
                                                            <div>
                                                                <strong>{item.policyNo || '-'}</strong>
                                                                <span>{item.row?.Client || item.row?.clientName || '-'}</span>
                                                            </div>
                                                            <div className="invoice-adjustment-values">
                                                                <small>Default Brokerage: {formatPercent(item.basePercentage)}%</small>
                                                                <small>Net Premium: {formatCurrency(item.netPremium)}</small>
                                                                <small>Statement Commission: {formatCurrency(item.statement)}</small>
                                                                <small>Taxable Commission: {formatCurrency(item.taxableStatementCommission)}</small>
                                                                <small>Current Expected: {formatCurrency(item.currentExpectedCommission)}</small>
                                                                <small>Difference: {formatCurrency(item.difference)}</small>
                                                            </div>
                                                            <label>
                                                                <span>Adjustment Type</span>
                                                                <select
                                                                    value={item.currentAdjustmentType}
                                                                    onChange={(event) => {
                                                                        const adjustmentType = event.target.value;
                                                                        setCommissionAdjustments((current) => ({
                                                                            ...current,
                                                                            [item.policyNo]: {
                                                                                ...current[item.policyNo],
                                                                                adjustmentType,
                                                                                adjustmentPercentage: current[item.policyNo]?.adjustmentPercentage ?? item.currentAdjustmentPercentage
                                                                            }
                                                                        }));
                                                                    }}
                                                                >
                                                                    <option value="add">Add</option>
                                                                    <option value="subtract">Subtract</option>
                                                                </select>
                                                            </label>
                                                            <label>
                                                                <span>Adjustment %</span>
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    step="0.01"
                                                                    value={item.currentAdjustmentPercentage}
                                                                    onChange={(event) => {
                                                                        const adjustmentPercentage = event.target.value;
                                                                        setCommissionAdjustments((current) => ({
                                                                            ...current,
                                                                            [item.policyNo]: {
                                                                                ...current[item.policyNo],
                                                                                adjustmentType: current[item.policyNo]?.adjustmentType ?? item.currentAdjustmentType,
                                                                                adjustmentPercentage
                                                                            }
                                                                        }));
                                                                    }}
                                                                />
                                                            </label>
                                                            <div className="invoice-final-rate">
                                                                Final Commission: <strong>{formatPercent(item.currentPercentage)}%</strong>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="invoice-mini-btn"
                                                                onClick={() => {
                                                                    setCommissionAdjustments((current) => ({
                                                                        ...current,
                                                                        [item.policyNo]: {
                                                                            adjustmentType: item.suggestedAdjustmentType,
                                                                            adjustmentPercentage: String(item.suggestedAdjustmentPercentage)
                                                                        }
                                                                    }));
                                                                }}
                                                            >
                                                                Use Suggested {item.suggestedAdjustmentType === 'subtract' ? 'Subtract' : 'Add'} {item.suggestedAdjustmentPercentage}%
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="invoice-mini-btn is-primary"
                                                                onClick={() => savePolicyBrokerageAdjustment(item)}
                                                                disabled={savingPolicyBrokerage[item.policyNo]}
                                                            >
                                                                {savingPolicyBrokerage[item.policyNo] ? 'Saving...' : 'Save To Policy'}
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {validationMissingPolicies.length > 0 && (
                                            <div className="invoice-validation-block">
                                                <h3>Missing Selected Policies</h3>
                                                <p>{validationMissingPolicies.map((item) => item.policyNo).join(', ')}</p>
                                            </div>
                                        )}

                                        {validationExtraRows.length > 0 && (
                                            <div className="invoice-validation-block">
                                                <h3>Extra Rows In Statement</h3>
                                                <p>{validationExtraRows.map((item) => item.policyNo || `Row ${item.rowNumber}`).join(', ')}</p>
                                            </div>
                                        )}

                                        {validationResult.status === 'Failed' && (
                                            <div className="invoice-validation-actions">
                                                <span>Invoice download is blocked until validation passes.</span>
                                                {uploadFile && (
                                                    <button
                                                        className="invoice-btn invoice-btn-primary"
                                                        type="button"
                                                        onClick={() => handleInvoiceDocUpload()}
                                                        disabled={uploading}
                                                    >
                                                        <RefreshCcw size={17} />
                                                        Revalidate With Adjustments
                                                    </button>
                                                )}
                                                {isAdminUser && uploadFile && (
                                                    <button
                                                        className="invoice-btn invoice-btn-light"
                                                        type="button"
                                                        onClick={() => handleInvoiceDocUpload({ forceProceed: true })}
                                                        disabled={uploading}
                                                    >
                                                        <AlertCircle size={17} />
                                                        Proceed With Warning
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                        <div className="invoice-modal-actions">
                                            <button
                                                type="button"
                                                onClick={() => setShowValidationModal(false)}
                                            >
                                                Close
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="uploaded-docs-section">
                            <div className="invoice-card-heading">
                                <div>
                                    <h2><Download size={18} /> Uploaded Documents</h2>
                                    <p>Track validated statements, downloaded invoices, and commission remittance status.</p>
                                </div>
                                <span className="invoice-selection-pill">{invoiceSummary.pendingDocs} pending</span>
                            </div>
                            <div className="uploaded-docs-list">
                                <table className="invoice-report-table">
                                    <thead>
                                        <tr>
                                            <th>Uploaded At</th>
                                            <th>Type</th>
                                            <th>File Name</th>
                                            <th>Insurer</th>
                                            <th>Selected Txn</th>
                                            <th>Validation</th>
                                            <th>Status</th>
                                            <th>Statment</th>
                                            <th>Downloaded Invoice</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {uploadedDocs.length === 0 ? (
                                            <tr>
                                                <td colSpan={10} className="invoice-empty-state">
                                                    <UploadCloud size={34} />
                                                    <strong>No uploaded documents found</strong>
                                                    <span>Validated invoice uploads will appear here.</span>
                                                </td>
                                            </tr>
                                        ) : uploadedDocs.map((doc) => (
                                            <tr key={doc._id}>
                                                <td>{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleString() : ''}</td>
                                                <td>{doc.fileType}</td>
                                                <td>{doc.originalName}</td>
                                                <td>{doc.insurerName || ''}</td>
                                                <td>{doc.selectedTransactionCount || 0}</td>
                                                <td>
                                                    <span
                                                        className={`invoice-status-badge ${['Validated', 'Validated With Warning'].includes(doc.validationSummary?.status || '')
                                                            ? 'status-remited'
                                                            : (doc.validationSummary?.status || '') === 'Failed'
                                                                ? 'status-cancelled'
                                                                : 'status-pending'
                                                            }`}
                                                    >
                                                        {doc.validationSummary?.status || (doc.fileType === 'pdf' ? 'Attached' : 'Pending')}
                                                    </span>
                                                    {doc.validationSummary?.mismatchCount > 0 && (
                                                        <small className="invoice-log-note">{doc.validationSummary.mismatchCount} mismatch</small>
                                                    )}
                                                </td>
                                                <td>
                                                    <span
                                                        className={`invoice-status-badge ${(doc.status || 'Commission Pending') === 'Remited'
                                                            ? 'status-remited'
                                                            : (doc.status || '') === 'Cancelled'
                                                                ? 'status-cancelled'
                                                                : ['Partially Received', 'Sent to Insurer'].includes(doc.status || '')
                                                                    ? 'status-partial'
                                                                    : 'status-pending'
                                                            }`}
                                                    >
                                                        {doc.status || 'Commission Pending'}
                                                    </span>
                                                    <select
                                                        value={doc.status || 'Commission Pending'}
                                                        onChange={(e) => handleStatusOptionChange(doc, e.target.value)}
                                                    >
                                                        <option value="Draft">Draft</option>
                                                        <option value="Validated">Validated</option>
                                                        <option value="Invoice Generated">Invoice Generated</option>
                                                        <option value="Downloaded">Downloaded</option>
                                                        <option value="Sent to Insurer">Sent to Insurer</option>
                                                        <option value="Commission Pending">Commission Pending</option>
                                                        <option value="Partially Received">Partially Received</option>
                                                        <option value="Remited">Remited</option>
                                                        <option value="Cancelled">Cancelled</option>
                                                        <option value="View Details">View Details</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    {doc.filePath ? (
                                                        <a
                                                            href={getDocumentViewUrl(doc.filePath)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            View
                                                            <LinkIcon size={13} />
                                                        </a>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </td>
                                                <td>
                                                    {doc.downloadedInvoicePath ? (
                                                        <a
                                                            href={getDocumentViewUrl(doc.downloadedInvoicePath)}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                        >
                                                            {doc.downloadedInvoiceName || doc.invoiceNumber || 'Downloaded Invoice'}
                                                            <Printer size={13} />
                                                        </a>
                                                    ) : '-'}
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="invoice-delete-btn"
                                                        onClick={() => handleDeleteUploadedDoc(doc._id)}
                                                    >
                                                        <Trash2 size={14} /> Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {showRemitModal && (
                            <div className="invoice-modal-backdrop">
                                <div className="invoice-modal">
                                    <h4>Remited Details</h4>
                                    <div className="invoice-modal-grid">
                                        <div>
                                            <label>Transaction ID</label>
                                            <input
                                                type="text"
                                                value={remitForm.transactionId}
                                                onChange={(e) => setRemitForm((p) => ({ ...p, transactionId: e.target.value }))}
                                            />
                                        </div>
                                        <div>
                                            <label>Amount</label>
                                            <input
                                                type="number"
                                                value={remitForm.amount}
                                                onChange={(e) => setRemitForm((p) => ({ ...p, amount: e.target.value }))}
                                            />
                                        </div>
                                        <div>
                                            <label>Date</label>
                                            <input
                                                type="date"
                                                value={remitForm.date}
                                                onChange={(e) => setRemitForm((p) => ({ ...p, date: e.target.value }))}
                                            />
                                        </div>
                                        <div>
                                            <label>Remarks</label>
                                            <input
                                                type="text"
                                                value={remitForm.remarks}
                                                onChange={(e) => setRemitForm((p) => ({ ...p, remarks: e.target.value }))}
                                            />
                                        </div>
                                    </div>
                                    <div className="invoice-modal-actions">
                                        <button onClick={submitRemittedStatus}>Save</button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowRemitModal(false);
                                                setActiveDoc(null);
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {showDetailsModal && (
                            <div className="invoice-modal-backdrop">
                                <div className="invoice-modal">
                                    <h4>Uploaded Document Details</h4>
                                    <div className="invoice-details">
                                        <p><strong>Status:</strong> {activeDoc?.status || 'Commission Pending'}</p>
                                        <p><strong>Validation:</strong> {activeDoc?.validationSummary?.status || '-'}</p>
                                        <p><strong>Matched:</strong> {activeDoc?.validationSummary?.matchedCount ?? '-'}</p>
                                        <p><strong>Mismatches:</strong> {activeDoc?.validationSummary?.mismatchCount ?? '-'}</p>
                                        <p><strong>Expected Commission:</strong> {formatCurrency(activeDoc?.validationSummary?.totalExpectedCommission || 0)}</p>
                                        <p><strong>Expected GST:</strong> {formatCurrency(activeDoc?.validationSummary?.totalExpectedGst || 0)}</p>
                                        <p><strong>Expected TDS:</strong> {formatCurrency(activeDoc?.validationSummary?.totalExpectedTds || 0)}</p>
                                        <p><strong>Expected Receivable:</strong> {formatCurrency(activeDoc?.validationSummary?.totalExpectedReceivable || 0)}</p>
                                        {(activeDoc?.validationDetails?.mismatchedRows || []).length > 0 && (
                                            <div className="invoice-details-errors">
                                                <strong>Validation Errors</strong>
                                                {(activeDoc.validationDetails.mismatchedRows || []).slice(0, 8).map((item, index) => (
                                                    <span key={`${item.policyNo || 'row'}-${index}`}>
                                                        {item.policyNo || `Row ${item.rowNumber || '-'}`}: {(item.statuses || []).join(', ') || 'Mismatch'} - {(item.reasons || []).join(' | ')}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        <p><strong>Transaction ID:</strong> {activeDoc?.remittanceDetails?.transactionId || '-'}</p>
                                        <p><strong>Amount:</strong> {activeDoc?.remittanceDetails?.amount ?? '-'}</p>
                                        <p>
                                            <strong>Date:</strong>{' '}
                                            {activeDoc?.remittanceDetails?.date
                                                ? new Date(activeDoc.remittanceDetails.date).toLocaleDateString()
                                                : '-'}
                                        </p>
                                        <p><strong>Remarks:</strong> {activeDoc?.remittanceDetails?.remarks || '-'}</p>
                                    </div>
                                    <div className="invoice-modal-actions">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowDetailsModal(false);
                                                setActiveDoc(null);
                                            }}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
};

export default InvoiceReports;
